import React, { Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';

import Login from '/features/login/Login';
import { RequireAuthGuard } from 'RequireAuth';
import { DefaultAuthGuard } from 'DefaultAuth';
import ErrorBoundary from '../common/ErrorBoundary';
import FullStory from '../../config/fullstory';
import { DynamicImports } from './DynamicImports';
import { EmptyComponent } from './Empty';
import { useLoggedInUpdateUser } from './useLoggedInUpdateUser';
import { useSelector } from 'react-redux';
import { QRPreviewAuthGuard } from '/components/QRPreviewAuth';

const NotFound = () => (
	<center>
		<h1>PAGE NOT FOUND 404</h1>
	</center>
);

const getOS = () => {
	let userAgent = window.navigator.userAgent,
		platform = window.navigator.platform,
		macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
		windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
		iosPlatforms = ['iPhone', 'iPad', 'iPod'],
		os = null;

	if (macosPlatforms.indexOf(platform) !== -1) {
		os = 'Mac';
	} else if (iosPlatforms.indexOf(platform) !== -1) {
		os = 'iOS';
	} else if (windowsPlatforms.indexOf(platform) !== -1) {
		os = 'Windows';
	} else if (/Android/.test(userAgent)) {
		os = 'Android';
	} else if (!os && /Linux/.test(platform)) {
		os = 'Linux';
	}

	return os;
};

const BeatComponentRedirectToAppProvider = () => {
	if (getOS() === 'Mac' || getOS() === 'iOS') {
		window.location.href = 'https://apps.apple.com/us/app/connectrn/id989863718';
	} else {
		window.location.href = 'https://play.google.com/store/apps/details?id=com.connectrn';
	}

	return null;
};

const Terms = (props) => (
	<DynamicImports load={() => import(/* webpackChunkName: "terms" */ 'Terms')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const LoyaltyRewards = (props) => (
	<DynamicImports load={() => import(/* webpackChunkName: "loyalty-rewards" */ './LoyaltyRewards')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const Maintenance = (props) => (
	<DynamicImports load={() => import(/* webpackChunkName: "maintenance" */ 'Maintenance')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const ErrorReporting = (props) => (
	<DynamicImports load={() => import(/* webpackChunkName: "error-reporting" */ '../ErrorReporting')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);
const FeedbackButton = (props) => (
	<DynamicImports load={() => import(/* webpackChunkName: "feedback" */ '../common/FeedbackButton')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const NPSModal = (props) => (
	<DynamicImports load={() => import(/* webpackChunkName: "nps-modal" */ '../NPS/NPSModal')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const NursePublicMyShifts = (props) => (
	<DynamicImports load={() => import(/* webpackChunkName: "nurse-my-shifts" */ '../Nurse/PublicMyShifts')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const OnboardingVerification = (props) => (
	<DynamicImports
		load={() => import(/* webpackChunkName: "onboarding-verification" */ '../Nurse/OnboardingVerification')}
	>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const PolicyAppLink = (props) => (
	<DynamicImports load={() => import('../Nurse/PolicyAppLink')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const NursePublicMyEarnings = (props) => (
	<DynamicImports load={() => import(/* webpackChunkName: "nurse-my-earnings" */ '../Nurse/PublicMyEarnings')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const NursePublicVGSCollectForm = (props) => (
	<DynamicImports load={() => import('../Nurse/VGSCollect/PublicVGSCollectForm')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const NursePublicVGSPINChange = (props) => (
	<DynamicImports load={() => import('../Nurse/VGSCollect/PublicVGSPINChange')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);
const NursePublicVGSPINSet = (props) => (
	<DynamicImports load={() => import('../Nurse/VGSCollect/PublicVGSPINSet')}>
		{(Component) => (Component === null ? <EmptyComponent /> : <Component {...props} />)}
	</DynamicImports>
);

const Admin = React.lazy(() => import(/* webpackChunkName: "admin-portal" */ '../Admin/AdminPortal'));
const SupervisorPortal = React.lazy(() =>
	import(/* webpackChunkName: "supervisor-portal" */ '../Supervisor/SupervisorPortal')
);

const UnavailableQR = React.lazy(() =>
	import(/* webpackChunkName: "supervisor-unavailable-qr" */ '../QRCICO/UnavailableQR')
);

const App = () => {
	const user = useSelector((state) => state.user);

	useLoggedInUpdateUser(user);

	return (
		<div className="custom-height">
			<ErrorBoundary>
				<FullStory />
				<Suspense fallback={<EmptyComponent />}>
					<Switch>
						<Route exact path="/">
							<DefaultAuthGuard>
								<Login />
							</DefaultAuthGuard>
						</Route>
						<Route path="/loyalty-rewards" strict={true}>
							<RequireAuthGuard>
								<LoyaltyRewards />
							</RequireAuthGuard>
						</Route>
						<Route exact path="/terms">
							<DefaultAuthGuard>
								<Terms />
							</DefaultAuthGuard>
						</Route>

						<Route path="/admin">
							<RequireAuthGuard>
								<Admin />
							</RequireAuthGuard>
						</Route>

						<Route path="/supervisor">
							<SupervisorPortal />
						</Route>
						<Route exact path="/nurse/publicmyshift">
							<NursePublicMyShifts />
						</Route>
						<Route exact path="/onboarding/verification">
							<OnboardingVerification />
						</Route>
						<Route exact path="/nurse/publicpolicies">
							<PolicyAppLink />
						</Route>
						<Route exact path="/nurse/publicmyearnings">
							<NursePublicMyEarnings />
						</Route>
						<Route exact path="/nurse/publicvgscollect">
							<NursePublicVGSCollectForm />
						</Route>
						<Route exact path="/nurse/publicvgspinset">
							<NursePublicVGSPINSet />
						</Route>
						<Route exact path="/nurse/publicvgspinchange">
							<NursePublicVGSPINChange />
						</Route>
						<Route exact path="/maintenance">
							<DefaultAuthGuard>
								<Maintenance />
							</DefaultAuthGuard>
						</Route>

						<Route path="/beat">
							<BeatComponentRedirectToAppProvider />
						</Route>

						<Route exact path="/unavailableqr">
							<QRPreviewAuthGuard>
								<UnavailableQR />
							</QRPreviewAuthGuard>
						</Route>

						<Route path="*">
							<NotFound />
						</Route>
					</Switch>
				</Suspense>
				<FeedbackButton />
				<ErrorReporting />
				<NPSModal />
			</ErrorBoundary>
		</div>
	);
};

export default App;
